/**
 * Project Helpers
 * Utility functions for working with design projects
 */

/**
 * Get the SID/ASID (project owner) from a project's teamMembers
 * @param {Object} project - Design project object with teamMembers array
 * @returns {Object|null} User object with role and supervisor info, or null
 */
export const getProjectSid = (project) => {
  if (!project?.teamMembers || !Array.isArray(project.teamMembers)) {
    return null;
  }

  const sidMember = project.teamMembers.find(
    tm => (tm.roleType === 'sid' || tm.roleType === 'asid') && tm.isActive !== false
  );

  if (!sidMember?.user) return null;

  // Return user with role info and supervisor (for ASIDs)
  return {
    ...sidMember.user,
    role: sidMember.roleType, // 'sid' or 'asid'
    supervisor: sidMember.user.sid || null // The SID this ASID reports to
  };
};

/**
 * Get the SID/ASID ID from a project's teamMembers
 * @param {Object} project - Design project object with teamMembers array
 * @returns {string|null} SID/ASID user ID or null
 */
export const getProjectSidId = (project) => {
  const sid = getProjectSid(project);
  return sid?.id || null;
};

/**
 * Check if a project has a SID/ASID assigned
 * @param {Object} project - Design project object with teamMembers array
 * @returns {boolean}
 */
export const hasProjectSid = (project) => {
  return getProjectSid(project) !== null;
};

/**
 * Get the display name for a project's SID/ASID
 * @param {Object} project - Design project object with teamMembers array
 * @returns {string} Display name or 'Unassigned'
 */
export const getProjectSidName = (project) => {
  const sid = getProjectSid(project);
  if (!sid) return 'Unassigned';
  return sid.nickname || sid.fullName || 'Unknown';
};
